// src/App.js
import React, { useEffect, useContext } from 'react';
import EContractSignPage from './pages/EContractSignPage.jsx';
import ContractTracker from './pages/ContractTracker.jsx';
import { ContractStatusProvider } from './contexts/ContractStatusContext';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate
} from 'react-router-dom';
import NavigationBar                from './components/Navbar';
import Footer                       from './components/Footer';
import Home                         from './pages/Home';
import AboutPage                    from './pages/AboutPage';
import ServicesPage                 from './pages/ServicesPage';
import PrivacyPolicyPage            from './pages/PrivacyPolicyPage';
import TermsOfServicePage           from './pages/TermsOfServicePage';
import DemoLandingPage              from './pages/DemoLandingPage';
import CRNADemoPage                 from './pages/CRNADemoPage';
import InterestedPage               from './pages/CRNAInterestPage';
import ContactPage                  from './pages/ContactPage';
import Login                        from './pages/Login';
import CRNAProfilePage              from './pages/CRNAProfilePage';
import GroupIntakePage              from './pages/GroupIntakePage';
import GroupProviderSwipePage       from './pages/GroupProviderSwipePage';
import TestGroupIntake              from './components/GroupIntake/TestGroupIntake';
import GroupMatches                 from './pages/GroupMatches';
import FacilityWaitlistPage         from './pages/FacilityWaitlistPage';
import ListingReviewPage            from './pages/ListingReviewPage';
import AdminDashboard               from './pages/AdminDashboard';
import Register                     from './pages/Register';
import RSVP                         from './pages/RSVP';
import UnauthorizedPage             from './pages/UnauthorizedPage';
import PremiumUpgradePage           from './pages/PremiumUpgradePage';
import PaymentSuccessPage           from './pages/PaymentSuccessPage';
import PaymentCancelPage            from './pages/PaymentCancelPage';
import BillingManagementPage        from './pages/BillingManagementPage';
import RoleBasedRoute               from './components/RoleBasedRoute';
import AuthDebug                    from './components/AuthDebug';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import AOS                          from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import featureFlagService from './services/featureFlagService';

// Define roles for our application
export const ROLES = {
  ADMIN: 'ADMIN',
  CRNA: 'CRNA',
  GROUP: 'GROUP',
};

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);

  useEffect(() => {
    // if user is logged in and they land on a public page, redirect to the appropriate dashboard
    const publicPaths = ['/', '/login', '/register'];
    
    if (auth?.token && auth?.user?.role && publicPaths.includes(location.pathname)) {
      // Route based on role
      if (auth.user.role === ROLES.ADMIN) {
        navigate('/admin', { replace: true });
      } else if (auth.user.role === ROLES.GROUP) {
        navigate('/group-intake', { replace: true });
      } else {
        // Default to profile for CRNA role
        navigate('/profile', { replace: true });
      }
    }
  }, [auth?.token, auth?.user?.role, location.pathname, navigate]);

  const isDemoPage    = location.pathname.startsWith('/demo');
  const isProfilePage = location.pathname === '/profile';

  return (
    <div style={{ paddingBottom: isDemoPage && !isProfilePage ? '100px' : 0 }}>
      {/* hide nav/footer on demo & profile */}
      {!isDemoPage && <NavigationBar />}
      {children}
      {!isDemoPage && !isProfilePage && <Footer />}
    </div>
  );
};

function App() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    
    // Initialize feature flag service
    // Replace 'Your_Environment_ID' with your actual Flagsmith environment ID
    // or use an environment variable
    const flagsmithEnvId = process.env.REACT_APP_FLAGSMITH_ENV_ID || 'development_environment';
    featureFlagService.init(flagsmithEnvId).then(() => {
      console.log('Feature flags initialized');
    }).catch((error) => {
      console.error('Failed to initialize feature flags:', error);
    });
  }, []);

  return (
    <AuthProvider>
      <ContractStatusProvider>
        <Router>
          <Layout>
          <Routes>
            {/* Public routes - anyone can access */}
            <Route path="/"            element={<Home />} />
            <Route path="/about"       element={<AboutPage />} />
            <Route path="/services"    element={<ServicesPage />} />
            <Route path="/contact"     element={<ContactPage />} />
            <Route path="/login"       element={<Login />} />
            <Route path="/register"    element={<Register />} />
            <Route
              path="/upgrade"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <PremiumUpgradePage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/billing-management"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <BillingManagementPage />
                </RoleBasedRoute>
              }
            />
            <Route path="/privacy-policy"    element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service"  element={<TermsOfServicePage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            
            {/* Payment result pages */}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment-cancel" element={<PaymentCancelPage />} />

            {/* CRNA Routes - Only CRNA and ADMIN can access */}
            <Route
              path="/profile"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <CRNAProfilePage />
                </RoleBasedRoute>
              }
            />
            
            <Route
              path="/facility-waitlist" 
              element={
                <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <FacilityWaitlistPage />
                </RoleBasedRoute>
              } 
            />
            
            <Route
              path="/rsvp"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <RSVP />
                </RoleBasedRoute>
              }
            />

            <Route
              path="/contract-tracker"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <ContractTracker />
                </RoleBasedRoute>
              }
            />

            {/* GROUP Routes - Only GROUP and ADMIN can access */}
            <Route
              path="/group-intake"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <GroupIntakePage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/contracts/:contractId/sign"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <EContractSignPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/group-provider-swipe"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <GroupProviderSwipePage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/group-provider-swipe/:listingId"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <GroupProviderSwipePage />
                </RoleBasedRoute>
              }
            />
            
            <Route
              path="/group-matches"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <GroupMatches />
                </RoleBasedRoute>
              }
            />


            {/* ADMIN Only Routes */}
            <Route
              path="/admin" 
              element={
                <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                  <>
                    <AuthDebug />
                    <AdminDashboard />
                  </>
                </RoleBasedRoute>
              } 
            />
            
            <Route
              path="/listings/review" 
              element={
                <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                  <ListingReviewPage />
                </RoleBasedRoute>
              } 
            />

            {/* Demo pages - available to all */}
            <Route path="/demo"                  element={<DemoLandingPage />} />
            <Route path="/demo/crna"             element={<CRNADemoPage />} />
            <Route path="/demo/interested-groups" element={<InterestedPage />} />
            <Route path="/auth-debug"            element={<AuthDebug />} />
            <Route
              path="/test-group-intake"
              element={
                <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                  <TestGroupIntake />
                </RoleBasedRoute>
              }
            />
          </Routes>
        </Layout>
      </Router>
      </ContractStatusProvider>
    </AuthProvider>
  );
}

export default App;
